/**
 * Quick Actions - Dashboard action buttons
 * @module components/starnexus/dashboard/QuickActions
 *
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Button from '@amzn/awsui-components-console/button';
import Box from '@amzn/awsui-components-console/box';

import { useAuthStore } from '../../../stores/starnexus/authStore';
import { hasPermission as checkPermission } from '../../../types/starnexus/team';
import type { Permission } from '../../../types/starnexus/team';

export const QuickActions: React.FC = () => {
  const navigate = useNavigate();
  const role = useAuthStore((state) => state.role);

  const actions: Array<{
    label: string;
    icon: string;
    href: string;
    description: string;
    ariaLabel: string;
    permission?: Permission;
  }> = [
    {
      label: 'View Processing Queue',
      icon: 'status-in-progress',
      href: '/processing',
      description: 'Monitor active jobs',
      ariaLabel: 'View the file processing queue and monitor active jobs'
    },
    {
      label: 'Manage Team',
      icon: 'group',
      href: '/team',
      description: 'Manage team members',
      ariaLabel: 'Manage team members and permissions',
      permission: 'manage_team'
    }
  ];

  return (
    <Container header={<Header variant="h2">Quick Actions</Header>}>
      <SpaceBetween size="m">
        {actions.map((action) => {
          // Check permission if required
          if (action.permission && (!role || !checkPermission(role, action.permission))) {
            return null;
          }

          return (
            <div key={action.label}>
              <Button variant="normal" iconName={action.icon as any} onClick={() => navigate(action.href)} fullWidth ariaLabel={action.ariaLabel}>
                {action.label}
              </Button>
              <Box variant="small" color="text-body-secondary" padding={{ top: 'xxs', left: 'l' }}>
                {action.description}
              </Box>
            </div>
          );
        })}
      </SpaceBetween>
    </Container>
  );
};

export default QuickActions;
